import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { AppShell } from "@/components/layout/AppShell";
import { Loader } from "@/components/common/Loader";

import Login from "@/pages/Login";
import EmployeeDashboard from "@/pages/EmployeeDashboard";
import Leads from "@/pages/Leads";
import NewLead from "@/pages/NewLead";
import LeadDetail from "@/pages/LeadDetail";
import Dealerships from "@/pages/Dealerships";
import DealershipDetail from "@/pages/DealershipDetail";
import TeamDashboard from "@/pages/TeamDashboard";
import Team from "@/pages/Team";
import EmployeeDetail from "@/pages/EmployeeDetail";
import Reports from "@/pages/Reports";
import More from "@/pages/More";

function FullLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <Loader label="Loading RVSF CRM…" />
    </div>
  );
}

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
}

function RequireLeader({ children }) {
  const { user } = useAuth();
  if (user?.role !== "team_leader") return <Navigate to="/dashboard" replace />;
  return children;
}

function RootRedirect() {
  const { user } = useAuth();
  return <Navigate to={user?.role === "team_leader" ? "/team" : "/dashboard"} replace />;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<RootRedirect />} />
        <Route path="dashboard" element={<EmployeeDashboard />} />
        <Route path="leads" element={<Leads />} />
        <Route path="leads/new" element={<NewLead />} />
        <Route path="leads/:id" element={<LeadDetail />} />
        <Route path="leads/:id/edit" element={<NewLead editMode />} />
        <Route path="dealerships" element={<Dealerships />} />
        <Route path="dealerships/:id" element={<DealershipDetail />} />
        <Route path="team" element={<RequireLeader><TeamDashboard /></RequireLeader>} />
        <Route path="team/employees" element={<RequireLeader><Team /></RequireLeader>} />
        <Route path="team/employees/:id" element={<RequireLeader><EmployeeDetail /></RequireLeader>} />
        <Route path="reports" element={<RequireLeader><Reports /></RequireLeader>} />
        <Route path="more" element={<More />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-center" richColors closeButton />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
